import React, { useState, useEffect } from "react";
import { Form, FormGroup, Label, Input, Button, Alert } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AuthUser from "./AuthUser";
import { BASE_URL } from "../../server";
import sha256 from "crypto-js/sha256";
import getHeaders from "../../utils/handleHeaderFunc";

const LoginForm = ({
  registrazioneProp,
  // Props per firstname
  firstname: externalFirstname,
  setFirstname: externalSetFirstname,
  // Props per lastname
  lastname: externalLastname,
  setLastname: externalSetLastname,
  // Props per phoneNumber
  phoneNumber: externalPhoneNumber,
  setPhoneNumber: externalSetPhoneNumber,
  // Props per pin
  pin: externalPin,
  setPin: externalSetPin,
  // Props per email
  email: externalEmail,
  setEmail: externalSetEmail,
}) => {
  const location = useLocation();
  const registrazione = location?.state?.registrazione;
  const isMobile = sessionStorage.getItem("mobile") || false;
  const { http, setToken } = AuthUser();

  // Stato interno per i campi
  const [internalFirstname, setInternalFirstname] = useState("");
  const [internalLastname, setInternalLastname] = useState("");
  const [internalPhoneNumber, setInternalPhoneNumber] = useState("");
  const [internalPin, setInternalPin] = useState("");
  const [internalEmail, setInternalEmail] = useState("");

  // Usa i valori passati via props se definiti, altrimenti usa lo stato interno
  const firstname =
    externalFirstname !== undefined ? externalFirstname : internalFirstname;
  const setFirstname = externalSetFirstname || setInternalFirstname;
  const lastname =
    externalLastname !== undefined ? externalLastname : internalLastname;
  const setLastname = externalSetLastname || setInternalLastname;
  const phoneNumber =
    externalPhoneNumber !== undefined
      ? externalPhoneNumber
      : internalPhoneNumber;
  const setPhoneNumber = externalSetPhoneNumber || setInternalPhoneNumber;
  const pin = externalPin !== undefined ? externalPin : internalPin;
  const setPin = externalSetPin || setInternalPin;
  const email = externalEmail !== undefined ? externalEmail : internalEmail;
  const setEmail = externalSetEmail || setInternalEmail;

  // Parsing della query string per il redirect
  const { redirectTo } = useLocation()
    .search.substring(1)
    .split("&")
    .reduce((params, param) => {
      const [key, value] = param.split("=");
      params[key] = value;
      return params;
    }, {});

  console.log("redirectTo:", redirectTo);

  // Handler per i cambiamenti nei campi
  const handleFirstnameChange = (e) => {
    setFirstname(e.target.value);
  };

  const handleLastnameChange = (e) => {
    setLastname(e.target.value);
  };

  const handlePhoneNumberChange = (e) => {
    setPhoneNumber(e.target.value);
  };

  const handlePinChange = (e) => {
    setPin(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRegistrazione, setIsRegistrazione] = useState(
    registrazione || registrazioneProp || false,
  );

  const navigate = useNavigate();

  const validateForm = () => {
    if (!firstname || !lastname || !email) {
      setError("Inserisci nome, cognome ed email.");
      return false;
    }
    const trimmedEmail = email.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(trimmedEmail)) {
      setError("Inserisci un indirizzo email valido.");
      return false;
    }
    return true;
  };

  const submitForm = async (
    e,
    firstname,
    lastname,
    phoneNumber,
    pin,
    email,
  ) => {
    e.preventDefault();
    console.log("Submitting form with:", {
      firstname,
      lastname,
      phoneNumber,
      pin,
      email,
    });
    setLoading(true);
    setError(null);

    if (!validateForm()) {
      setLoading(false);
      return;
    }
    let randomPin = Math.floor(100000 + Math.random() * 900000);
    let trimmedPin = "";
    try {
      // Trim e lowercase per firstname, lastname, email e pulizia degli altri campi
      if (!pin) {
        trimmedPin = randomPin;
      } else {
        trimmedPin = pin?.trim();
      }

      const trimmedFirstname = firstname?.trim().toLowerCase();
      const trimmedLastname = lastname?.trim().toLowerCase();
      const trimmedPhoneNumber = phoneNumber?.trim();
      const trimmedEmail = email?.trim().toLowerCase();
      const dataToHash = `${trimmedFirstname}${trimmedLastname}${trimmedPhoneNumber}${trimmedPin}`;
      const hashedData = sha256(dataToHash).toString();
      const token = btoa(`${hashedData}:${hashedData}`);

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Basic ${token}`,
      };
      // Prima richiesta di login con il valore di isRegistrazione corrente
      let login = await axios.post(
        `${BASE_URL}/user/login`,
        { registration: isRegistrazione },
        { headers },
      );

      // Se il response indica "Utente non registrato", riprova con registration: true
      if (login.data && login.data.esito === 99 && login.data.client_id === 0) {
        // Se non abbiamo già effettuato la registrazione, riproviamo
        if (!isRegistrazione) {
          login = await axios.post(
            `${BASE_URL}/user/login`,
            { registration: true },
            { headers },
          );
          console.log("Retry login with registration true:", login);
        }
      }

      // Se il login ha successo, cioè se il profilo contiene un id, procedi con l'update
      if (login?.data?.profile && login?.data?.profile?.id) {
        sessionStorage.setItem("user", JSON.stringify(login.data.profile));
        sessionStorage.setItem("basic_token", token);
        sessionStorage.setItem("client_id", login.data.profile.id);

        try {
          const apiUrl = `${BASE_URL}/update_user`;
          const updateHeaders = {
            "Content-Type": "application/json",
            Authorization: `Basic ${token}`,
          };
          const data = {
            profile: {
              id: login.data.profile.id,
              firstname,
              lastname,
              email,
              phone: phoneNumber,
              competence_id: 4,
            },
          };
          const response = await axios.put(apiUrl, data, {
            headers: updateHeaders,
          });
          if (response.status === 200) {
            sessionStorage.setItem(
              "user",
              JSON.stringify({
                ...login.data.profile,
                firstname,
                lastname,
                email,
                phone: phoneNumber,
              }),
            );
            if (redirectTo) {
              navigate(redirectTo);
            } else {
              navigate("/prenotazione-appuntamento/user");
            }
          } else {
            setError("Login fallito. Verifica le tue credenziali.");
          }
        } catch (error) {
          console.error(error);
          setError("Si è verificato un errore durante il login.");
        } finally {
          setLoading(false);
        }
      } else {
        setError("Login fallito. Verifica le tue credenziali.");
      }
    } catch (error) {
      console.error(error);
      setError("Si è verificato un errore durante il login.");
    }
    setLoading(false);
  };

  // ====== AGGIUNTA: Prefill da cookie spid_profile ======

  // Helper: legge un cookie per nome
  const getCookie = (name) => {
    try {
      const pairs = document.cookie.split(";").map((c) => c.trim());
      for (const p of pairs) {
        if (!p) continue;
        const [k, ...v] = p.split("=");
        if (decodeURIComponent(k) === name) {
          return v.join("=");
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  // Helper: prova a decodificare JSON eventualmente url-encoded
  const parsePossiblyEncodedJson = (str) => {
    if (!str) return null;
    try {
      console.log("Decoding string:", str);
      const decoded = decodeURIComponent(str);
      console.log("Decoded string:", decoded);
      const parsed = JSON.parse(decoded);
      console.log("Parsed JSON!:", parsed);
      return parsed;
    } catch {
      try {
        return JSON.parse(str);
      } catch {
        return null;
      }
    }
  };

  useEffect(() => {
    const raw = getCookie("spid_profile");
    console.log("spid_profile cookie raw:", raw);
    const parsed = parsePossiblyEncodedJson(raw);
    console.log("spid_profile cookie parsed:", parsed);

    if (parsed && typeof parsed === "object") {
      // Mappature tipiche SPID: nome, cognome, telefono, email
      if (parsed.nome && !firstname) setFirstname(parsed.nome);
      if (parsed.cognome && !lastname) setLastname(parsed.cognome);
      if ((parsed.telefono || parsed.phone) && !phoneNumber)
        setPhoneNumber(parsed.telefono || parsed.phone);
      if (parsed.email && !email) setEmail(parsed.email);
      // Di solito il PIN non arriva da SPID; non lo tocco.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== FINE AGGIUNTA ======

  return (
    <div className="container">
      <div className="row">
        {!!redirectTo && (
          <>
            <div
              class="mb-3 flex"
              style={{
                marginTop: "2.22rem",
                fontSize: "16px",
                fontWeight: "bold",
                color: "#146995",
              }}
            >
              <a href="/" className="flex items-center mr-1">
                Home
              </a>
              <span> / </span>
              <a
                className="flex items-center ml-1"
                href="/prenotazione-appuntamento"
              >
                Prenotazione appuntamento
              </a>
            </div>
            <h2>Richiedente</h2>
            <b>Informazioni sul richiedente</b>
          </>
        )}
        <div className={isMobile ? "padding-mobile" : "shadow p-5"}>
          {!redirectTo && (
            <div className="mb-5">
              <h2>Richiedente</h2>
              <b>Informazioni sul richiedente</b>
            </div>
          )}

          {error && <Alert color="danger">{error}</Alert>}

          <Form
            onSubmit={(e) =>
              submitForm(e, firstname, lastname, phoneNumber, pin, email)
            }
          >
            <FormGroup>
              <Label for="firstname" className="font-semibold">
                Nome
              </Label>
              <Input
                type="text"
                name="firstname"
                id="firstname"
                placeholder="Inserisci il tuo nome"
                value={firstname}
                onChange={handleFirstnameChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="lastname" className="font-semibold">
                Cognome
              </Label>
              <Input
                type="text"
                name="lastname"
                id="lastname"
                placeholder="Inserisci il tuo cognome"
                value={lastname}
                onChange={handleLastnameChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="email" className="font-semibold">
                Indirizzo email
              </Label>
              <Input
                type="email"
                name="email"
                id="email"
                placeholder="Inserisci il tuo indirizzo email"
                value={email}
                onChange={handleEmailChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="phoneNumber" className="font-semibold">
                Numero di telefono
              </Label>
              <Input
                type="text"
                name="phoneNumber"
                id="phoneNumber"
                placeholder="Inserisci il tuo numero di telefono"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="pin" className="font-semibold">
                PIN (Opzionale: Inserisci il tuo pin oppure scegli un PIN.
                Utilizzando il tuo PIN potrai poi gestire i tuoi appuntamenti)
              </Label>
              <Input
                type="password"
                name="pin"
                id="pin"
                placeholder="Inserisci il tuo PIN"
                value={pin}
                onChange={handlePinChange}
              />
            </FormGroup>

            {redirectTo && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  marginTop: "32px",
                }}
              >
                {loading ? (
                  <Button type="submit" color="primary" disabled>
                    <span className="spinner-border spinner-border-sm"></span>
                  </Button>
                ) : (
                  <Button
                    style={{ fontSize: "16px" }}
                    type="submit"
                    color="primary"
                    disabled={
                      !firstname || !lastname || !email || !phoneNumber || !pin
                    }
                  >
                    {isRegistrazione ? "Conferma" : "Accedi"}
                  </Button>
                )}
              </div>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
